import { z } from 'zod';
import { zoneGeoJsonPointSchema } from './pricing.schemas';

/**
 * The trip event log (gitaumoses4/chukua#43). Trip status is derived from these events, so the list
 * is the vocabulary the M4/M5 producers write. The `trip_events_*_check` constraints in
 * `dispatch.schema.ts` hard-code the same values; adding one means a migration. The milestone
 * names (`departed` … `unloaded`) are provisional until the first producer exists. The five that cause a state change the lifecycle
 * has no other word for (`pre_trip_failed`, `exception_cleared`, `aborted`, `closed_out`, `ready_to_invoice`) came with gitaumoses4/chukua#44;
 * `trip-transitions.ts` says which event moves a trip where. `geofence_entered` / `geofence_exited` (gitaumoses4/chukua#89) are facts a GPS
 * feed writes when the truck crosses a geofence; they move no state and a phone may not write them (not in `DRIVER_EVENT_TYPES`).
 * `driver_handed_over` (the office, when it moves a trip to another driver) and `handover_acknowledged` (the new driver's phone) are facts too
 * (gitaumoses4/chukua#80): a handover leaves the milestone where it was, so the new driver resumes from it.
 * `declined` is the driver refusing a dispatched trip (gitaumoses4/chukua#70): a fact like `accepted`, with a reason in its payload, and it moves no state.
 * `empty_returned` is the driver handing the empty container back, the last of the six milestone buttons (gitaumoses4/chukua#72), and `milestone_retracted`
 * is the driver undoing a tapped milestone: a correction (see below) that replaces the milestone it names, so the milestone no longer stands. Both
 * move no state.
 * `pre_trip_checked` is a submitted pre-trip check, pass or fail (gitaumoses4/chukua#71): a fact that moves no state, with the check in its payload.
 * The server alone derives the two events that follow from it: `pre_trip_failed` when a critical item failed, and `pre_trip_cleared`
 * (`pre_trip_failed` → `dispatched`) when a later check has none, so a phone never types a state.
 * `post_trip_recorded` is the driver's final check on return, with the odometer and fuel readings in its payload (gitaumoses4/chukua#77, `post-trip.schemas.ts`):
 * a fact that moves no state. `closed_out` stays the office's; the fact only ends the driver's part of a delivered trip.
 * A correction is any event whose `corrects_event_id` is set; its type says what the corrected fact now is.
 */
export const TRIP_EVENT_TYPES = [
  'trip_opened',
  'dispatched',
  'accepted',
  'driver_confirmed',
  'departed',
  'stop_reached',
  'arrived',
  'loaded',
  'unloaded',
  'location_pinged',
  'fuel_stop',
  'exception',
  'pod_captured',
  'pre_trip_failed',
  'exception_cleared',
  'aborted',
  'closed_out',
  'ready_to_invoice',
  'geofence_entered',
  'geofence_exited',
  'driver_handed_over',
  'handover_acknowledged',
  'declined',
  'empty_returned',
  'milestone_retracted',
  'post_trip_recorded',
  'pre_trip_checked',
  'pre_trip_cleared',
] as const;
export type TripEventType = (typeof TRIP_EVENT_TYPES)[number];
export const tripEventTypeSchema = z.enum(TRIP_EVENT_TYPES);

/** Who or what wrote the event: office staff, the driver's app, a GPS feed or the platform itself. */
export const TRIP_EVENT_SOURCES = ['office', 'driver_app', 'gps', 'system'] as const;
export type TripEventSource = (typeof TRIP_EVENT_SOURCES)[number];
export const tripEventSourceSchema = z.enum(TRIP_EVENT_SOURCES);

/**
 * Appending an event by hand (the office path; the driver app's goes through the sync API, #59-61). `source` is not in the
 * body: the server says who is writing, so an office user cannot write a `driver_app` event into the evidence.
 * `serverTime` is not either (a trigger stamps it). `.strict()`, so a caller that sends one is told.
 */
export const appendTripEventInputSchema = z
  .object({
    eventType: tripEventTypeSchema,
    deviceTime: z.iso.datetime({ offset: true }),
    location: zoneGeoJsonPointSchema.optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
    idempotencyKey: z.string().min(1).max(200).optional(),
    correctsEventId: z.uuid().optional(),
  })
  .strict();
export type AppendTripEventInput = z.infer<typeof appendTripEventInputSchema>;
